import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

function Centered({ children }) {
  return <div className="min-h-screen flex items-center justify-center p-6 text-center text-muted-foreground">{children}</div>;
}

export default function ClientApproval() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(false);
  const [msg, setMsg] = useState("");
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/public/jobs/${id}/approval-info?token=${encodeURIComponent(token)}`)
      .then((r) => setInfo(r.data)).catch(() => setErr(true));
  }, [id, token]);

  const approve = async () => {
    setBusy(true);
    try { await api.post(`/public/jobs/${id}/approve?token=${encodeURIComponent(token)}`); setDone("approved"); }
    catch { toast.error("This link is invalid or has expired."); } finally { setBusy(false); }
  };
  const requestChanges = async () => {
    setBusy(true);
    try { await api.post(`/public/jobs/${id}/request-changes?token=${encodeURIComponent(token)}`, { message: msg }); setDone("changes"); }
    catch { toast.error("This link is invalid or has expired."); } finally { setBusy(false); }
  };

  if (err) return <Centered>This approval link is invalid or has expired. Please contact your valuer.</Centered>;
  if (!info) return <Centered>Loading…</Centered>;
  const existing = info.approval?.status;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white rounded-xl border border-border shadow-sm p-6 space-y-4" data-testid="client-approval-page">
        <div className="text-xs uppercase tracking-widest text-accent font-bold">{info.firm_name}</div>
        <h1 className="font-display text-2xl text-primary">Draft Valuation Approval</h1>
        <div className="text-sm text-muted-foreground">Ref <span className="font-mono">{info.reference}</span> · {info.applicant_name}</div>
        {info.property_address && <div className="text-sm text-primary">{info.property_address}</div>}

        <a href={`${API}/public/jobs/${id}/valuation-report.pdf?token=${encodeURIComponent(token)}&draft=1`} target="_blank" rel="noreferrer">
          <Button variant="outline" className="rounded-full w-full" data-testid="ca-view-draft">View draft report (PDF)</Button>
        </a>

        {(done === "approved" || existing === "approved") ? (
          <div className="text-center text-teal-700 font-medium py-2" data-testid="ca-approved">✓ Thank you — your approval has been recorded.</div>
        ) : done === "changes" ? (
          <div className="text-center text-amber-700 font-medium py-2" data-testid="ca-changes-done">Your change request has been sent to the valuer.</div>
        ) : (
          <>
            <Button onClick={approve} disabled={busy} className="rounded-full w-full bg-primary hover:bg-[#113A64]" data-testid="ca-approve-btn">
              {busy ? "Submitting…" : "Approve this draft"}
            </Button>
            <div className="space-y-2 pt-1">
              <Textarea rows={3} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Or describe the changes you need…" data-testid="ca-msg" />
              <Button onClick={requestChanges} disabled={busy} variant="outline" className="rounded-full w-full" data-testid="ca-changes-btn">Request changes</Button>
            </div>
          </>
        )}
        <p className="text-[11px] text-muted-foreground text-center pt-2">Secure approval link · {info.bank_name}</p>
      </div>
    </div>
  );
}
